import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { getDesignAuditHistoryAction } from 'Redux/Actions/designAuditActions';
import { getDesignAuditCountsAction } from 'Redux/Actions/designAuditActions';

import FilesService from 'services/files';
import { getSingleDesignAudit } from 'services/webdashboardReviews';
import WebDashboardReviews from 'services/webdashboardReviews';

import { DESIGN_AUDIT_STATUS, PAGE_SIZE, PDF_STATUS } from 'utlis/constants';
import { errorReactToastify } from 'utlis/toasts';
import { isFigmaURL } from 'utlis/validators';

import DesignSelection from './components/DesignSelection';
import ReviewContext from './components/ReviewContext';
import ReviewResults from './components/ReviewResults';

const CreateReview = ({ onClose, existingReviewData }) => {
  const dispatch = useDispatch();

  const authUser = useSelector(state => state.userStore);

  const [pollingFailedCounter, setPollingFailedCounter] = useState(0);
  const [showReviewResults, setShowReviewResults] = useState(false);
  const [productDescription, setProductDescription] = useState('');
  const [isReviewComplete, setIsReviewComplete] = useState(false);
  const [hasTAMMGuideline, setHasTAMMGuideline] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalFiles, setOriginalFiles] = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [title, setTitle] = useState('');

  const pollingIntervalRef = useRef(null);

  const isFormValid = useMemo(
    () => productDescription.trim() !== ''
      && title.trim() !== ''
      && uploadedImages.length > 0,
    [productDescription, title, uploadedImages]
  );

  const pollDesignAudit = useCallback(async designUuid => {
    try {
      const data = await getSingleDesignAudit(designUuid);

      setReviewData(data);

      if (data.status === DESIGN_AUDIT_STATUS.COMPLETED) {
        fetchDesignAuditHistory();
        setIsReviewComplete(true);
        setReviewData(data);

        // Check if PDF generation is complete or failed
        const pdfProcess = data.post_processes?.find(val => val.process_type === 1);

        if (pdfProcess && (pdfProcess.status === PDF_STATUS.READY || pdfProcess.status === PDF_STATUS.FAILED)) {
          // PDF generation complete (status 2) or failed (status 3)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
        // Continue polling if PDF is still processing (status 1) or pending (status 0)
      }
      else if (data.status === DESIGN_AUDIT_STATUS.FAILURE) {
        // Handle failure state
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Show error toast
        errorReactToastify(
          'The review has failed, please try again later',
          {
            autoClose: 5000,
            position: 'bottom-center',
            hideProgressBar: true,
            style: { backgroundColor: '#FDF0F0', width: '470px' },
          }
        );

        // Close the popup after 5 seconds
        setTimeout(() => {
          onClose();
        }, 5000);
      }
    }
    catch (error) {
      // handle the state where polling fails
      const newCounter = pollingFailedCounter + 1;

      setPollingFailedCounter(newCounter);

      // Don't continue polling if we've hit the limit
      if (newCounter >= 3) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Show error toast
        errorReactToastify(
          'There is an issue with the review, please contact support',
          {
            autoClose: 5000,
            position: 'bottom-center',
            hideProgressBar: true,
            style: { backgroundColor: '#FDF0F0', width: '470px' },
          }
        );

        // Close the popup after 5 seconds
        setTimeout(() => {
          onClose();
        }, 5000);
      }
    }
  }, [onClose, pollingFailedCounter]);

  const startPolling = useCallback((designUuid, intervalMs = 20000) => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll immediately
    pollDesignAudit(designUuid);

    // Set up interval for subsequent polls
    pollingIntervalRef.current = setInterval(() => {
      pollDesignAudit(designUuid);
    }, intervalMs);
  }, [pollDesignAudit]);

  // Handle existing review data
  useEffect(() => {
    if (existingReviewData) {
      // Set review data and populate form
      setReviewData(existingReviewData);

      // If review is completed, show results
      if (existingReviewData.status === DESIGN_AUDIT_STATUS.COMPLETED) {
        setIsReviewComplete(true);
        setShowReviewResults(true);
      }

      if (existingReviewData.status === DESIGN_AUDIT_STATUS.IN_PROGRESS) {
        const createdAt = new Date(existingReviewData.created_at);
        const now = new Date();
        const hourInMs = 60 * 60 * 1000;

        if ((now - createdAt) < hourInMs) {
          startPolling(existingReviewData.uuid);
        }
        setShowReviewResults(true);
      }

      // Set uploaded images from design_images
      if (existingReviewData.audit_data?.design_images) {
        const formattedImages = existingReviewData.audit_data.design_images.map((imageUrl, index) => ({
          name: `Review Image ${index + 1}`,
          uid: `existing-${index}`,
          preview: imageUrl,
          url: imageUrl,
          progress: 100,
          isProgressLoading: false,
          isExistingImage: true,
        }));

        setUploadedImages(formattedImages);
      }
    }
  }, [existingReviewData]);

  // Restore product description from localStorage when component mounts (only for new reviews)
  useEffect(() => {
    if (existingReviewData) {
      return;
    } // Skip for existing reviews
    const storedProductDescription = localStorage.getItem('designReview_productDescription');
    const storedTitle = localStorage.getItem('designReview_title');

    if (storedProductDescription) {
      setProductDescription(storedProductDescription || '');
      // Clear the stored value after restoring
      localStorage.removeItem('designReview_productDescription');
    }

    if (storedTitle) {
      setTitle(storedTitle || '');
      // Clear the stored value after restoring
      localStorage.removeItem('designReview_title');
    }
  }, []);

  // Monitor pollingFailedCounter and handle failure case
  useEffect(() => {
    if (pollingFailedCounter >= 3) {
      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Show error message
      errorReactToastify(
        'Review has failed. Please try again later.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );

      // Close popup after 5 seconds
      setTimeout(() => {
        onClose();
      }, 5000);
    }
  }, [pollingFailedCounter, onClose]);

  // Cleanup polling on component unmount
  useEffect(() => () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  return (
    <div className="relative w-full h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex items-center justify-between px-6 py-3 h-16">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-900">
            {title || existingReviewData?.title || 'New design review'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-10 h-10 bg-white rounded-full
            hover:bg-gray-50 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5l10 10" stroke="#606776" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 flex justify-center py-20">
        {showReviewResults && !isSubmitting
          ? (
            <ReviewResults
              isReviewComplete={isReviewComplete}
              reviewData={reviewData}
              title={title}
              productDescription={productDescription}
            />
          )
          : (
            <ReviewContext
              handleSubmitReview={handleSubmitReview}
              isFormValid={isFormValid}
              productDescription={productDescription}
              setProductDescription={setProductDescription}
              isSubmitting={isSubmitting}
              title={title}
              setTitle={setTitle}
              hasTAMMGuideline={hasTAMMGuideline}
              setHasTAMMGuideline={setHasTAMMGuideline}
            />
          )}

        <DesignSelection
          setUploadedImages={setUploadedImages}
          setOriginalFiles={setOriginalFiles}
          uploadingImages={uploadingImages}
          setFigmaUrl={setFigmaUrl}
          figmaUrl={figmaUrl}
          uploadedImages={uploadedImages}
          showReviewResults={showReviewResults}
          productDescription={productDescription}
          isSubmitting={isSubmitting}
          title={title}
        />
      </div>
    </div>
  );

  async function handleSubmitReview() {
    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      let finalImageUrls = [];
      let finalFigmaUrl = null;

      // If we have a Figma URL, use it
      if (figmaUrl && isFigmaURL(figmaUrl)) {
        finalFigmaUrl = figmaUrl;
      }
      else if (originalFiles.length > 0) {
        setUploadingImages(true);
        // Upload all files to S3 and wait for all to complete
        const uploadPromises = originalFiles.map(async file => {
          const url = await FilesService.uploadFileFromLocal(file);

          return url;
        });

        finalImageUrls = await Promise.all(uploadPromises);
        setUploadingImages(false);
      }

      setShowReviewResults(true);

      const response = await WebDashboardReviews.submitReview({
        figmaUrl: finalFigmaUrl,
        imageUrls: finalImageUrls.length > 0 ? finalImageUrls : null,
        product: productDescription,
        title: title,
        authUser: authUser,
        hasTAMMGuideline: hasTAMMGuideline,
      });

      setReviewData(response.data);
      fetchDesignAuditHistory();

      // Start polling for design audit status
      const designAuditUuid = response.data?.design_audit_uuid;

      startPolling(designAuditUuid);
    }
    catch (err) {
      errorReactToastify(
        'Failed to submit review. Please try again.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );
      setShowReviewResults(false);
    }
    finally {
      setIsSubmitting(false);
    }
  }

  // Fetch design audit history
  async function fetchDesignAuditHistory(page = 1, days = 30) {
    try {
      await Promise.all([
        dispatch(getDesignAuditHistoryAction(days, page, PAGE_SIZE)),
        dispatch(getDesignAuditCountsAction()),
      ]);
    }
    catch (error) {
      Sentry.captureException(error);
    }
  }
};

CreateReview.propTypes = {
  onClose: PropTypes.func.isRequired,
  existingReviewData: PropTypes.object,
};

export default CreateReview;
